import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Refund } from '../../data/refunds';
import { colors, hairline, radii, spacing } from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * Where a refund has got to.
 *
 * Two steps, always both drawn: raised, then in the account. The second is
 * hollow until the bank confirms it, with the expected date under it - that
 * gap is five to seven working days, and an app that says "Refunded" on day
 * one produces a support ticket on day two from somebody looking at a
 * statement that disagrees.
 *
 * A partial refund shows its split rather than one net figure. "₹450 refunded"
 * beside a ₹499 payment is a number somebody has to reverse-engineer; the fee
 * on its own line answers the question before it is asked.
 */

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

function RefundTimeline({ refund }: { refund: Refund }) {
  const settled = refund.status === 'settled';

  return (
    <View style={styles.card}>
      <View style={styles.head}>
        <View style={styles.mark}>
          <Icon name="refund" size={18} color={colors.primary} />
        </View>
        <View style={styles.headText}>
          <Text variant="subtitle">{rupees(refund.amount)} refunded</Text>
          <Text variant="caption" color={colors.inkMuted}>
            To {refund.method}
          </Text>
        </View>
      </View>

      {/* The split, only where something was actually withheld. */}
      {refund.fee > 0 ? (
        <View style={styles.split}>
          <Text variant="caption" color={colors.inkMuted}>
            Cancellation fee kept
          </Text>
          <Text variant="caption" color={colors.inkBody}>
            {rupees(refund.fee)}
          </Text>
        </View>
      ) : null}

      <View style={styles.steps}>
        <Step
          done
          title="Refund raised"
          detail={refund.initiatedOn}
        />
        <View style={[styles.track, settled ? styles.trackOn : null]} />
        <Step
          done={settled}
          title={settled ? 'In your account' : 'Reaching your account'}
          detail={
            settled
              ? (refund.settledOn as string)
              : `Expected by ${refund.expectedBy}`
          }
        />
      </View>

      {/* Said plainly while it is in transit, and dropped once it lands. */}
      {settled ? null : (
        <View style={styles.note}>
          <Icon name="clock" size={14} color={colors.inkMuted} />
          <Text variant="caption" color={colors.inkMuted} style={styles.grow}>
            Banks take 5–7 working days to post a refund. We will tell you the
            moment it lands.
          </Text>
        </View>
      )}

      <Text variant="caption" color={colors.inkSubtle}>
        Reference {refund.id}
      </Text>
    </View>
  );
}

/** One end of the track: a dot, a title and the date it happened or is due. */
function Step({
  done,
  title,
  detail,
}: {
  done: boolean;
  title: string;
  detail: string;
}) {
  return (
    <View style={styles.step}>
      <View style={[styles.dot, done ? styles.dotOn : null]}>
        {done ? (
          <Icon name="check" size={11} color={colors.onPrimary} strokeWidth={3} />
        ) : null}
      </View>
      <Text variant="labelSm" color={done ? colors.ink : colors.inkMuted}>
        {title}
      </Text>
      <Text variant="caption" color={colors.inkMuted}>
        {detail}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  headText: {
    flex: 1,
    gap: 2,
  },
  split: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  steps: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  step: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.xs,
  },
  dot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
    borderWidth: 1,
    borderColor: colors.borderStrong,
  },
  dotOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  /* Between the two dots, at their height. Filled only once both ends are. */
  track: {
    height: 2,
    flex: 1,
    marginTop: 9,
    borderRadius: 1,
    backgroundColor: colors.border,
  },
  trackOn: {
    backgroundColor: colors.primary,
  },
  note: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  grow: {
    flex: 1,
  },
});

export default RefundTimeline;
